import { useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";

/**
 * The claim details page's motion, defined once.
 *
 * Two things this fixes about the old page. The slide between sections was a
 * CSS keyframe gated on `mobile`, so on a desktop the page had no motion at all
 * — content simply appeared. And the keyframes hard-coded a direction, needing
 * a separate `[dir="rtl"]` block to un-mirror them, which was easy to forget on
 * the next animation added.
 *
 * Here the horizontal distance is signed by the reading direction, so one
 * definition is correct in both languages, and every variant is shared by the
 * phone and the desktop layouts.
 */

// A decelerating curve — quick to leave, soft to land. Matches the entrance the
// notification surfaces use, so the two screens feel like one product.
const EASE = [0.22, 0.61, 0.36, 1];

export const useClaimMotion = () => {
  // Respects the OS "reduce motion" setting. Everything below collapses to a
  // plain opacity change rather than being switched off entirely: with no
  // transition at all, a tab change gives no feedback that anything happened.
  const reduceMotion = useReducedMotion();
  const { i18n } = useTranslation();
  const rtl = i18n.resolvedLanguage === "ar";

  // Positive x means "toward the end of the line" in either direction.
  const dx = (distance) => (reduceMotion ? 0 : rtl ? -distance : distance);
  const dy = (distance) => (reduceMotion ? 0 : distance);

  const duration = reduceMotion ? 0.12 : 0.3;

  return {
    reduceMotion,
    rtl,

    // Header, stepper and section headings on first paint.
    rise: {
      initial: { opacity: 0, y: dy(12) },
      animate: { opacity: 1, y: 0 },
      transition: { duration, ease: EASE },
    },

    // Staggered list entrance. The children inherit `listItem` below; the
    // stagger is small on purpose — a long claim history should not take a
    // second and a half to finish arriving.
    listContainer: {
      initial: "hidden",
      animate: "shown",
      variants: {
        hidden: {},
        shown: {
          transition: {
            staggerChildren: reduceMotion ? 0 : 0.045,
            delayChildren: reduceMotion ? 0 : 0.02,
          },
        },
      },
    },

    listItem: {
      variants: {
        hidden: { opacity: 0, y: dy(10) },
        shown: { opacity: 1, y: 0, transition: { duration, ease: EASE } },
      },
    },

    /**
     * Tab panels. `direction` is +1 when moving to a later tab and -1 when
     * moving back, so the content travels the way the finger did.
     */
    panel: (direction) => ({
      initial: { opacity: 0, x: dx(direction * 24) },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: dx(direction * -24) },
      transition: { duration, ease: EASE },
    }),

    // Cards and other tappable rows. Kept subtle: these are list rows, not
    // buttons on a landing page.
    tappable: reduceMotion
      ? {}
      : { whileHover: { y: -2 }, whileTap: { scale: 0.99 } },

    // Dialogs.
    overlay: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: reduceMotion ? 0.1 : 0.2, ease: EASE },
    },

    dialog: {
      initial: { opacity: 0, scale: reduceMotion ? 1 : 0.97, y: dy(8) },
      animate: { opacity: 1, scale: 1, y: 0 },
      exit: { opacity: 0, scale: reduceMotion ? 1 : 0.98, y: dy(4) },
      transition: { duration: reduceMotion ? 0.12 : 0.22, ease: EASE },
    },
  };
};

export default useClaimMotion;
